/**
 * KAN-834: карточка клиента в приложении менеджера (/m/clients/:id):
 * шапка со звонком, итог, менеджер (тем, кто переназначает любых клиентов:
 * владелец, оператор, старший менеджер), переписка и звонки с записью.
 * KAN-852: блок "Контакты и данные" (Telegram, наш аккаунт, телефон, email,
 * досье и что выяснил бот под "Ещё"); взять в работу в блоке "Итог".
 * KAN-858: вверху "Что сделать" (причина внимания, задача, основное действие),
 * "Бот напишет" (касание бота с действиями), ручная переписка с полем ввода.
 * KAN-929: звонки менеджера в ленте переписки, отдельного блока "Звонки" нет.
 * KAN-940: карточка с key по клиенту: при переходе к другому клиенту состояние
 * (недописанный комментарий) не переезжает к нему, прежний сохраняется своему.
 * KAN-944: клиента взял другой менеджер (useClientAccess) - вместо карточки
 * MobileClientTakenByOther (кто взял, "К списку", несохранённый текст). Клиент
 * ответил 404 - устаревшая карточка не показывается, только "Клиент не найден";
 * пока доступ перепроверяется, загрузка.
 */
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { MobileBackButton } from '@/components/mobile/MobileBackButton';
import { ClientManagerSelect } from '@/components/clients/ClientManagerSelect';
import { MobileClientChat } from '@/components/mobile/MobileClientChat';
import { MobileClientContacts } from '@/components/mobile/MobileClientContacts';
import { MobileClientSummary } from '@/components/mobile/MobileClientSummary';
import { MobileClientTakenByOther } from '@/components/mobile/MobileClientTakenByOther';
import { MobileManagerTask } from '@/components/mobile/MobileManagerTask';
import { MobileNextBotTouch } from '@/components/mobile/MobileNextBotTouch';
import { MobileOutcomeBlock } from '@/components/mobile/MobileOutcomeBlock';
import { isNotFoundError } from '@/hooks/useCatalog';
import { useClientAccess } from '@/hooks/useClientAccess';
import { useClientManagerRights } from '@/hooks/useClientManagerRights';
import { useMobileOutcome } from '@/hooks/useMobileOutcome';
import { useClient } from '@/services/api/clientQueries';
import type { Client } from '@/types/client';

function ClientCard({ client }: { client: Client }): JSX.Element {
  const { reassignsAny } = useClientManagerRights(client.manager?.id);
  const outcome = useMobileOutcome(client);
  return (
    <>
      <MobileManagerTask client={client} work={outcome.work} busy={outcome.pending || outcome.saving} />
      <MobileClientSummary client={client} />
      <MobileNextBotTouch client={client} />
      <MobileOutcomeBlock client={client} outcome={outcome} />
      <MobileClientContacts client={client} />
      {reassignsAny && (
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <ClientManagerSelect clientId={client.id} manager={client.manager} />
        </section>
      )}
      <MobileClientChat client={client} />
    </>
  );
}

export function MobileClientPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { data: client, isLoading, isError, error } = useClient(id);
  const access = useClientAccess(id);
  const { takenBy } = access;
  const gone = isNotFoundError(error);
  // Клиент ответил 404: сначала ждём ответа доступа (может, его взял другой).
  const loading = !takenBy && (isLoading || (gone && access.checking && !access.notFound));
  const missing = !takenBy && !loading && (isError || !client);

  return (
    <div className="space-y-3 px-4 py-3" data-testid="mobile-client-page">
      <MobileBackButton />
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-label="Загрузка" />
        </div>
      )}
      {takenBy && <MobileClientTakenByOther key={id} clientId={id} takenBy={takenBy} />}
      {missing && <p className="py-8 text-center text-sm text-red-600">Клиент не найден</p>}
      {client && !takenBy && !gone && <ClientCard key={client.id} client={client} />}
    </div>
  );
}
